/**
 * Allocation Tracking Type Definitions
 * Based on docs/spec/allocation/07-integration.md Section 8.4
 *
 * Allocation tracking is an MVP+1 feature available on Pro/Family plans.
 * Feature entitlement: feature:allocation_tracking
 */

// ============================================================================
// Enum Types (match backend exactly)
// ============================================================================

export type AssetClass =
  | 'stocks_domestic'
  | 'stocks_international'
  | 'stocks_emerging'
  | 'bonds_government'
  | 'bonds_corporate'
  | 'bonds_municipal'
  | 'bonds_international'
  | 'real_estate'
  | 'commodities'
  | 'cash'
  | 'crypto'
  | 'alternatives'
  | 'other';

export type StrategicRole =
  | 'emergency_fund'
  | 'short_term_savings'
  | 'retirement_pretax'
  | 'retirement_roth'
  | 'retirement_taxable'
  | 'education'
  | 'healthcare'
  | 'general_investment';

export type TargetType =
  | 'exact_amount'
  | 'range'
  | 'months_expenses'
  | 'percentage_income';

export type DriftDirection = 'over' | 'under';

export type WarningSeverity = 'info' | 'warning' | 'alert';

export type WarningStatus = 'active' | 'dismissed' | 'resolved';

export type ClassificationSource =
  | 'user_override'
  | 'provider_data'
  | 'ticker_lookup'
  | 'security_type'
  | 'default';

export type ConfidenceLevel = 'high' | 'medium' | 'low';

export type HoldingSource = 'synced' | 'manual' | 'synthetic';

export type RiskProfile =
  | 'conservative'
  | 'moderately_conservative'
  | 'moderate'
  | 'moderately_aggressive'
  | 'aggressive'
  | 'custom';

export type AllocationWarningType =
  | 'portfolio_drift'
  | 'bucket_drift'
  | 'account_below_target'
  | 'account_above_target'
  | 'missing_classification'
  | 'unassigned_account';

// ============================================================================
// Display Name Mappings
// ============================================================================

export const ASSET_CLASS_DISPLAY_NAMES: Record<AssetClass, string> = {
  stocks_domestic: 'Domestic Stocks',
  stocks_international: 'International Stocks',
  stocks_emerging: 'Emerging Markets',
  bonds_government: 'Government Bonds',
  bonds_corporate: 'Corporate Bonds',
  bonds_municipal: 'Municipal Bonds',
  bonds_international: 'International Bonds',
  real_estate: 'Real Estate',
  commodities: 'Commodities',
  cash: 'Cash & Equivalents',
  crypto: 'Cryptocurrency',
  alternatives: 'Alternative Investments',
  other: 'Other',
};

export const STRATEGIC_ROLE_DISPLAY_NAMES: Record<StrategicRole, string> = {
  emergency_fund: 'Emergency Fund',
  short_term_savings: 'Short-Term Savings',
  retirement_pretax: 'Retirement (Pre-Tax)',
  retirement_roth: 'Retirement (Roth)',
  retirement_taxable: 'Retirement (Taxable)',
  education: 'Education',
  healthcare: 'Healthcare',
  general_investment: 'General Investment',
};

export const RISK_PROFILE_DISPLAY_NAMES: Record<RiskProfile, string> = {
  conservative: 'Conservative',
  moderately_conservative: 'Moderately Conservative',
  moderate: 'Moderate',
  moderately_aggressive: 'Moderately Aggressive',
  aggressive: 'Aggressive',
  custom: 'Custom',
};

export const TARGET_TYPE_DISPLAY_NAMES: Record<TargetType, string> = {
  exact_amount: 'Exact Amount',
  range: 'Range',
  months_expenses: 'Months of Expenses',
  percentage_income: 'Percentage of Income',
};

// ============================================================================
// Portfolio Allocation Types
// ============================================================================

export interface AssetClassAllocation {
  assetClass: AssetClass;
  displayName: string;
  currentAmount: string;
  currentPercentage: string;
  targetPercentage: string | null;
  driftPercentage: string | null;
  driftDirection: DriftDirection | null;
  holdingCount: number;
}

export interface PortfolioAllocation {
  totalValueUsd: string;
  totalHoldings: number;
  classifiedHoldings: number;
  unclassifiedHoldings: number;
  riskProfile: RiskProfile | null;
  allocations: AssetClassAllocation[];
  lastCalculatedAt: string;
}

// ============================================================================
// Bucket Allocation Types
// ============================================================================

export interface BucketAllocation {
  strategicRole: StrategicRole;
  displayName: string;
  currentAmount: string;
  currentPercentage: string;
  targetPercentage: string | null;
  driftPercentage: string | null;
  driftDirection: DriftDirection | null;
  accountCount: number;
  accountIds: string[];
}

export interface BucketAllocationSummary {
  totalValueUsd: string;
  buckets: BucketAllocation[];
  unassignedValueUsd: string;
  unassignedCount: number;
  unassignedAccountCount: number;
  warningsCount: number;
  lastCalculated: string | null;
}

// ============================================================================
// Account Target Types
// ============================================================================

/**
 * Response schema for a single account target.
 * Matches backend: AccountTargetResponse in schemas/allocation.py
 */
export interface AccountTargetResponse {
  id: string;
  accountId: string;
  targetType: TargetType;
  targetValue: string;
  targetValueMax: string | null;
  currency: string;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}

/**
 * Account with nested target - used in list responses.
 * Matches backend: AccountWithTarget in schemas/allocation.py
 */
export interface AccountWithTarget {
  accountId: string;
  accountName: string;
  currentBalanceUsd: string | null;
  target: AccountTargetResponse | null;
  targetStatus: 'below_target' | 'on_target' | 'above_target' | null;
  difference: string | null;
}

export interface AccountTargetsSummary {
  totalWithTargets: number;
  onTarget: number;
  belowTarget: number;
  aboveTarget: number;
}

// ============================================================================
// Holding Classification Types
// ============================================================================

export interface HoldingWithClassification {
  holdingId: string;
  accountId: string;
  accountName: string;
  securityName: string;
  ticker: string | null;
  quantity: string;
  currentValue: string;
  assetClass: AssetClass;
  classificationSource: ClassificationSource;
  confidence: ConfidenceLevel;
  needsReview: boolean;
  overrideReason: string | null;
  isManual?: boolean;
  isSynthetic?: boolean;
  holdingSource?: HoldingSource;
  currency?: string;
  currentValueUsd?: string;
}

// ============================================================================
// Manual Holding CRUD Types (WP-MANUAL-HOLD Phase 3)
// ============================================================================

/**
 * Request to create a manual holding.
 */
export interface CreateHoldingRequest {
  accountId: string;
  name: string;
  tickerSymbol?: string | null;
  quantity: string;  // Decimal as string
  price?: string | null;
  priceAsOf?: string | null;  // ISO date string
  costBasis?: string | null;
  currency?: string;
  currentValueUsd?: string | null;
  assetClass?: AssetClass | null;
}

/**
 * Request to update a manual holding.
 */
export interface UpdateHoldingRequest {
  name?: string;
  tickerSymbol?: string | null;
  quantity?: string;
  price?: string | null;
  priceAsOf?: string | null;
  costBasis?: string | null;
  currency?: string;
  currentValueUsd?: string | null;
}

/**
 * Detailed holding response.
 */
export interface HoldingDetail {
  holdingId: string;
  accountId: string;
  accountName: string;
  name: string;
  tickerSymbol: string | null;
  quantity: string;
  price: string | null;
  priceAsOf: string | null;
  value: string | null;
  currentValueUsd: string | null;
  costBasis: string | null;
  costBasisPerUnit: string | null;
  currency: string;
  assetClass: AssetClass;
  classificationSource: ClassificationSource;
  confidence: ConfidenceLevel;
  isManual: boolean;
  isSynthetic: boolean;
  holdingSource: HoldingSource;
  providerName: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface HoldingsSummary {
  totalHoldings: number;
  needsReviewCount: number;
  classifiedCount: number;
}

// ============================================================================
// Warning Types
// ============================================================================

export interface AllocationWarning {
  id: string;
  warningType: AllocationWarningType;
  severity: WarningSeverity;
  title: string;
  message: string;
  assetClass: AssetClass | null;
  strategicRole: StrategicRole | null;
  accountId: string | null;
  currentValue: string;
  targetValue: string | null;
  driftPercentage: string | null;
  status: WarningStatus;
  dismissedAt: string | null;
  dismissUntil: string | null;
  createdAt: string;
}

export interface WarningsSummary {
  total: number;
  byType: Record<AllocationWarningType, number>;
  bySeverity: Record<WarningSeverity, number>;
}

// ============================================================================
// Template Types
// ============================================================================

export interface AllocationTemplate {
  id: string;
  name: string;
  description: string;
  riskProfile: RiskProfile;
  isSystem: boolean;
  // Backend returns 'targets' field, frontend expects 'portfolioTargets'/'bucketTargets'
  // Support both for backwards compatibility
  portfolioTargets?: Record<AssetClass, string>;
  bucketTargets?: Record<StrategicRole, string>;
  targets?: Record<string, number>;  // Current backend format
}

// ============================================================================
// Request Types
// ============================================================================

export interface SetPortfolioTargetsRequest {
  targets?: Record<AssetClass, number>;
  riskProfile?: RiskProfile;
}

export interface SetBucketTargetsRequest {
  targets: Record<StrategicRole, number>;
}

export interface AssignAccountToBucketRequest {
  accountId: string;
  assignments: Record<StrategicRole, number>;
}

/**
 * Request schema for creating account target.
 * Matches backend: AccountTargetCreate in schemas/allocation.py
 */
export interface CreateAccountTargetRequest {
  accountId: string;
  targetType: TargetType;
  targetValue: string;
  targetValueMax?: string;
  currency?: string;
  notes?: string;
}

/**
 * Request schema for updating account target.
 * Matches backend: AccountTargetUpdate in schemas/allocation.py
 */
export interface UpdateAccountTargetRequest {
  targetType?: TargetType;
  targetValue?: string;
  targetValueMax?: string;
  currency?: string;
  notes?: string;
}

export interface ClassifyHoldingRequest {
  assetClass: AssetClass;
  overrideReason?: string;
}

export interface BulkClassifyHoldingsRequest {
  classifications: Array<{
    holdingId: string;
    assetClass: AssetClass;
  }>;
}

export interface DismissWarningRequest {
  dismissUntil?: string;
}

export interface ApplyTemplateRequest {
  applyPortfolio?: boolean;
  applyBuckets?: boolean;
}

// ============================================================================
// Response Types
// ============================================================================

export interface PortfolioAllocationResponse {
  allocation: PortfolioAllocation;
}

export interface BucketAllocationResponse {
  allocation: BucketAllocationSummary;
}

/**
 * Response schema for listing account targets.
 * Matches backend: TargetsListResponse in schemas/allocation.py
 */
export interface AccountTargetsResponse {
  targets: AccountWithTarget[];
  summary: AccountTargetsSummary;
}

export interface HoldingsListResponse {
  holdings: HoldingWithClassification[];
  count: number;
  classifiedCount: number;
  unclassifiedCount: number;
  needsReviewCount?: number;
}

export interface WarningsResponse {
  warnings: AllocationWarning[];
  summary: WarningsSummary;
}

export interface TemplatesResponse {
  systemTemplates: AllocationTemplate[];
  customTemplates: AllocationTemplate[];
}

export interface BulkClassifyResponse {
  updatedCount: number;
}

// ============================================================================
// Sankey Visualization Types
// ============================================================================

export interface AllocationSankeyNode {
  id: string;
  name: string;
  value: number;
  category: 'asset_class' | 'bucket' | 'account';
}

export interface AllocationSankeyLink {
  source: string;
  target: string;
  value: number;
}

export interface AllocationSankeyData {
  nodes: AllocationSankeyNode[];
  links: AllocationSankeyLink[];
}

// ============================================================================
// Utility Types
// ============================================================================

export interface AllocationListParams {
  page?: number;
  pageSize?: number;
}

export interface HoldingsListParams extends AllocationListParams {
  needsReview?: boolean;
  assetClass?: AssetClass;
  accountId?: string;
  includeSynthetic?: boolean;
}

export interface WarningsListParams {
  status?: WarningStatus;
  severity?: WarningSeverity;
  warningType?: AllocationWarningType;
}

// ============================================================================
// Historical Allocation Types (Time-Travel Queries)
// ============================================================================

/**
 * Snapshot source type - how the holding snapshot was created
 */
export type HoldingSnapshotSourceType =
  | 'aggregator'
  | 'statement'
  | 'manual'
  | 'system';

/**
 * Historical holding data at a point in time
 */
export interface HistoricalHolding {
  holdingId: string;
  accountId: string;
  tickerSymbol: string | null;
  securityName: string | null;
  quantity: string;
  price: string | null;
  valueUsd: string;
  costBasisUsd: string | null;
  allocationPercentage: string;
}

/**
 * Complete portfolio allocation at a historical date
 */
export interface HistoricalAllocation {
  asOfDate: string;
  totalValueUsd: string;
  holdings: HistoricalHolding[];
  holdingCount: number;
}

/**
 * Single data point in portfolio value series
 */
export interface PortfolioValuePoint {
  asOfDate: string;
  totalValueUsd: string;
  holdingCount: number;
}

/**
 * Portfolio value time series for charts
 */
export interface PortfolioValueSeries {
  startDate: string;
  endDate: string;
  granularity: 'daily' | 'weekly' | 'monthly';
  dataPoints: PortfolioValuePoint[];
  pointCount: number;
}

/**
 * Performance calculation between two dates
 */
export interface PerformanceResult {
  startDate: string;
  endDate: string;
  startValueUsd: string;
  endValueUsd: string;
  absoluteChangeUsd: string;
  percentageChange: string;
}

/**
 * Drift data point - allocation breakdown by ticker at a date
 */
export interface DriftDataPoint {
  asOfDate: string;
  totalValueUsd: string;
  holdingsByTicker: Record<string, string>;
}

/**
 * Drift history over time
 */
export interface DriftHistory {
  startDate: string;
  endDate: string;
  granularity: 'daily' | 'weekly' | 'monthly';
  dataPoints: DriftDataPoint[];
  pointCount: number;
}

/**
 * Holding snapshot - point-in-time record
 */
export interface HoldingSnapshot {
  id: string;
  holdingId: string;
  accountId: string;
  asOfDate: string;
  quantity: string;
  price: string | null;
  priceAsOf: string | null;
  valueUsd: string | null;
  costBasisUsd: string | null;
  tickerSymbol: string | null;
  securityName: string | null;
  sourceType: HoldingSnapshotSourceType;
  confidence: ConfidenceLevel;
  isPrimary: boolean;
  createdAt: string;
}

/**
 * History of snapshots for a single holding
 */
export interface HoldingHistory {
  holdingId: string;
  startDate: string;
  endDate: string;
  snapshots: HoldingSnapshot[];
  snapshotCount: number;
}

/**
 * Comparison item for allocation changes between two dates
 */
export interface AllocationComparisonItem {
  tickerSymbol: string;
  startValueUsd: string;
  endValueUsd: string;
  startPercentage: string;
  endPercentage: string;
  valueChangeUsd: string;
  percentagePointChange: string;
}

/**
 * Comparison of allocation between two dates
 */
export interface AllocationComparison {
  startDate: string;
  endDate: string;
  startTotalValueUsd: string;
  endTotalValueUsd: string;
  holdings: AllocationComparisonItem[];
  totalAbsoluteChangeUsd: string;
  totalPercentageChange: string;
}

// ============================================================================
// Historical Allocation Request Types
// ============================================================================

export interface HistoricalAllocationParams {
  targetDate: string;
  accountIds?: string[];
}

export interface PortfolioValueSeriesParams {
  startDate: string;
  endDate: string;
  accountIds?: string[];
  granularity?: 'daily' | 'weekly' | 'monthly';
}

export interface PerformanceParams {
  startDate: string;
  endDate: string;
  accountIds?: string[];
}

export interface DriftHistoryParams {
  startDate: string;
  endDate: string;
  accountIds?: string[];
  granularity?: 'daily' | 'weekly' | 'monthly';
}

export interface AllocationComparisonParams {
  startDate: string;
  endDate: string;
  accountIds?: string[];
}

export interface HoldingHistoryParams {
  holdingId: string;
  startDate: string;
  endDate: string;
}

// ============================================================================
// Tenant Profile Types (WP-ALLOC-CONFIG-1)
// ============================================================================

/**
 * Tenant allocation profile from GET /allocation/portfolio/profile
 * Controls how portfolio allocation is calculated
 *
 * IMPORTANT: NO tenantId field - backend excludes for security
 * @see services/core/app/schemas/allocation.py TenantProfileResponse
 */
export interface TenantProfileResponse {
  id: string;
  templateId: string | null;
  template: AllocationTemplate | null;
  customTargets: Record<AssetClass, number> | null;
  effectiveTargets: Record<AssetClass, number>;
  rebalanceThresholdPct: number;
  includeCash: boolean;
  includeRealEstate: boolean;
  createdAt: string;
  updatedAt: string;
}

/**
 * Create profile request for POST /allocation/portfolio/profile
 * All fields optional - server applies defaults
 */
export interface TenantProfileCreate {
  templateId?: string;
  customTargets?: Record<AssetClass, number>;
  rebalanceThresholdPct?: number;
  includeCash?: boolean;
  includeRealEstate?: boolean;
}

/**
 * Update profile request for PUT /allocation/portfolio/profile
 * All fields optional - only changed fields sent
 */
export interface TenantProfileUpdate {
  templateId?: string | null;
  customTargets?: Record<AssetClass, number> | null;
  rebalanceThresholdPct?: number;
  includeCash?: boolean;
  includeRealEstate?: boolean;
}
